import { useEffect, useMemo, useState, type Dispatch, type SetStateAction } from "react";
import { Alert, Box, Button, Checkbox, IconButton, MenuItem, Stack, TextField, Typography, useTheme } from "@mui/material";
import { Add, Block, CheckCircle, Delete, Edit, Sync } from "@mui/icons-material";
import { ColorField } from "@/components/ColorField";
import { FilterModal } from "@/components/FilterModal";
import { HoraField } from "@/components/HoraField";
import { SeloIntegracao } from "@/components/escala/SeloIntegracao";
import usePagination from "@/hooks/pagination";
import { ConfirmModal } from "@/components/ConfirmModal";
import { useNotification } from "@/context/NotificationContext";
import { useCrud } from "@/hooks/useCrud";
import { DataTable } from "@/components/DataTable";
import { CrudModal } from "@/components/crud/CrudModal";
import { CrudToolbar } from "@/components/crud/CrudToolbar";
import {
  ImportadorPlanilha,
  type ColunaImportacao,
  type LinhaPlanilha,
  type ResultadoImportacao,
} from "@/components/import/ImportadorPlanilha";
import { useLogger } from "@/hooks/useLogger";
import { useListaRemota } from "@/hooks/useListaRemota";
import { LogEventType } from "@/api/log.service";
import { useAuth } from "@/context/AuthContext";
import { capitalizar } from "@/utils/format";
import { SeloTipo } from "@/components/escala/EspelhoLinha";
import { estiloTipo, fundoDaHora } from "@/components/escala/tiposDia";
import {
  ativarCargaHoraria,
  excluirCargaHoraria,
  importarCargasHorarias,
  listCargasHorarias,
  saveCargaHoraria,
  type BlocoHorario,
  type CargaHoraria,
} from "@/api/escala.service";
import {
  BLOCOS,
  CAMPOS_BLOCO,
  DESCRICAO_CARGA_MAX,
  ROTULO_BLOCO,
  ROTULO_CAMPO,
  blocoParaEnvio,
  blocoVazio,
  chaveColunaHora,
  chaveDescricao,
  corDaJornada,
  corJornadaValida,
  corParaEnvio,
  escalasQueExigem,
  formatarBloco,
  juntarComE,
  mensagemDoReflexoDeCarga,
  podeExcluirCarga,
  resolverCargasParaImportar,
  validarCargaHoraria,
  type CampoBloco,
  type NomeBloco,
} from "@/components/escala/cargaHoraria";

type FiltroStatus = "todas" | "ativas" | "inativas";

// Exemplo do modelo: comércio com sábado até o meio-dia, sem domingo nem feriado.
const EXEMPLO_HORA: Record<string, string> = {
  [chaveColunaHora("util", "entrada")]: "08:00",
  [chaveColunaHora("util", "intervalo_inicio")]: "12:00",
  [chaveColunaHora("util", "intervalo_fim")]: "13:00",
  [chaveColunaHora("util", "saida")]: "18:00",
  [chaveColunaHora("sabado", "entrada")]: "08:00",
  [chaveColunaHora("sabado", "saida")]: "12:00",
};

// Uma coluna por horário, na ordem do formulário. Cor e blocos opcionais em branco valem como no formulário.
const colunasImportacaoCargas: ColunaImportacao[] = [
  { chave: "descricao", rotulo: "Descrição", obrigatoria: true, exemplo: "Comercial" },
  { chave: "cor", rotulo: "Cor da jornada (#rrggbb)" },
  ...BLOCOS.flatMap((bloco) =>
    CAMPOS_BLOCO.map((campo) => ({
      chave: chaveColunaHora(bloco, campo),
      rotulo: `${ROTULO_BLOCO[bloco]} - ${ROTULO_CAMPO[campo]}`,
      tipo: "hora" as const,
      exemplo: EXEMPLO_HORA[chaveColunaHora(bloco, campo)],
    })),
  ),
];

// A cor como sai na grade: a entrada do dia útil sobre o verde do T, do mesmo tamanho e com o mesmo
// ajuste de tema da célula. "Padrão" ou cor incompleta saem na cor normal do T.
function PreviaCorDaJornada({ cor, entrada }: { cor?: string; entrada?: string }) {
  const theme = useTheme();
  const hora = /^\d{2}:\d{2}$/.test(entrada ?? "") ? entrada! : "08:00";
  return (
    <Stack alignItems="center" spacing={0.5} sx={{ flexShrink: 0, pt: 1.25 }} data-testid="previa-cor-jornada">
      <SeloTipo
        tipo={hora}
        estilo={estiloTipo(theme, "T")}
        cor={corJornadaValida(cor) ? corDaJornada(cor!.trim(), fundoDaHora(theme)) : undefined}
      />
      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
        Na grade
      </Typography>
    </Stack>
  );
}

interface CargaHorariaFormProps {
  values: Partial<CargaHoraria>;
  setValues: Dispatch<SetStateAction<Partial<CargaHoraria>>>;
  isEditing: boolean;
  existentes: CargaHoraria[];
  // Os erros aparecem a partir da primeira tentativa de salvar e acompanham a digitação.
  mostrarErros: boolean;
}

function CargaHorariaForm({ values, setValues, existentes, mostrarErros }: CargaHorariaFormProps) {
  const erros = mostrarErros ? validarCargaHoraria(values, existentes) : [];
  const erroDo = (campo: string) => erros.find((e) => e.campo === campo)?.mensagem;

  // O bloco que uma escala ativa que usa a carga exige não pode ficar em branco.
  const gravada = values.id ? existentes.find((c) => c.id === values.id) : undefined;
  const escalasAtivas = gravada?.escalas_ativas;
  // Na carga criada pela integração, o dia útil e o sábado só mudam no sistema externo.
  const travadoPor = gravada?.travado_por;
  const travado = (bloco: NomeBloco) => Boolean(travadoPor) && (bloco === "util" || bloco === "sabado");

  const setCampo = (bloco: NomeBloco, campo: CampoBloco, valor: string) =>
    setValues((v) => ({ ...v, [bloco]: { ...(v[bloco] ?? {}), [campo]: valor } as BlocoHorario }));

  return (
    <>
      {travadoPor && (
        <Alert severity="info">Criada pela integração ({travadoPor}): o dia útil e o sábado só mudam lá.</Alert>
      )}

      <TextField
        label="Descrição"
        fullWidth
        required
        value={values.descricao ?? ""}
        onChange={(e) => setValues((v) => ({ ...v, descricao: e.target.value }))}
        error={!!erroDo("descricao")}
        helperText={erroDo("descricao")}
        slotProps={{ htmlInput: { maxLength: DESCRICAO_CARGA_MAX } }}
      />
      {/* Em branco é "Padrão": a hora sai na cor normal da fonte. */}
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <ColorField
          label="Cor da jornada"
          fullWidth
          placeholder="Padrão"
          valorPadrao=""
          value={values.cor ?? ""}
          onChange={(cor) => setValues((v) => ({ ...v, cor }))}
          error={!!erroDo("cor")}
          helperText={erroDo("cor")}
        />
        <PreviaCorDaJornada cor={values.cor} entrada={values.util?.entrada} />
      </Stack>
      {BLOCOS.map((bloco) => {
        const exigidoPor = bloco === "sabado" || bloco === "domingo" ? escalasQueExigem(bloco, escalasAtivas) : [];
        const opcional = bloco !== "util" && exigidoPor.length === 0;
        return (
          <Box key={bloco}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle2">
                {ROTULO_BLOCO[bloco]}
                {opcional && " (opcional)"}
              </Typography>
              <Stack direction="row" spacing={1}>
                {/* O feriado costuma ter o horário do domingo: um clique em vez de quatro campos. */}
                {bloco === "feriado" && (
                  <Button
                    size="small"
                    onClick={() => setValues((v) => ({ ...v, feriado: { ...(v.domingo as BlocoHorario) } }))}
                    disabled={blocoVazio(values.domingo)}
                  >
                    Copiar do domingo
                  </Button>
                )}
                {opcional && (
                  <Button
                    size="small"
                    onClick={() => setValues((v) => ({ ...v, [bloco]: null }))}
                    disabled={travado(bloco) || blocoVazio(values[bloco])}
                  >
                    Limpar
                  </Button>
                )}
              </Stack>
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              {CAMPOS_BLOCO.map((campo) => {
                const erro = erroDo(`${bloco}.${campo}`);
                return (
                  <HoraField
                    key={campo}
                    label={ROTULO_CAMPO[campo]}
                    ariaLabel={`${ROTULO_BLOCO[bloco]} — ${ROTULO_CAMPO[campo]}`}
                    size="small"
                    fullWidth
                    required={!opcional && (campo === "entrada" || campo === "saida")}
                    disabled={travado(bloco)}
                    value={values[bloco]?.[campo] ?? ""}
                    onChange={(hora) => setCampo(bloco, campo, hora)}
                    error={!!erro}
                    helperText={erro}
                  />
                );
              })}
            </Stack>
            {opcional && (
              <Typography variant="caption" color="text.secondary">
                {bloco === "feriado"
                  ? "Em branco: o feriado trabalhado fica sem horário (?) até ser resolvido na grade."
                  : `Em branco: a carga não tem horário de ${ROTULO_BLOCO[bloco].toLowerCase()}.`}
              </Typography>
            )}
            {exigidoPor.length > 0 && (
              <Typography variant="caption" color="text.secondary">
                Não pode ficar em branco: a carga é usada {exigidoPor.length > 1 ? "nas escalas" : "na escala"}{" "}
                {juntarComE(exigidoPor)}.
              </Typography>
            )}
          </Box>
        );
      })}
    </>
  );
}

export function EscalaCargasHorariasTab() {
  const { user } = useAuth();
  const clientId = user!.loggedClient!.id;
  const { notify } = useNotification();
  const { log } = useLogger();
  const { total, setTotal, page, setPage, rowsPerPage, setRowsPerPage } = usePagination();
  const { open, editing, handleCreate, handleEdit, handleClose } = useCrud<CargaHoraria>();
  const [tentouSalvar, setTentouSalvar] = useState(false);

  const filtrosIniciais = { descricao: "", status: "todas" as FiltroStatus };
  const [inputValues, setInputValues] = useState(filtrosIniciais);
  const [filtros, setFiltros] = useState(filtrosIniciais);

  const [alternando, setAlternando] = useState<CargaHoraria | null>(null);
  const [excluindo, setExcluindo] = useState<CargaHoraria | null>(null);

  // Lista inteira, filtrada aqui: a checagem de descrição repetida precisa de todas.
  const { itens: cargas, carregouAlgumaVez, recarregar } = useListaRemota(
    () => listCargasHorarias(clientId),
    [clientId],
    "Erro ao carregar cargas horárias",
  );

  const filtered = useMemo(() => {
    const termo = chaveDescricao(filtros.descricao);
    return cargas.filter(
      (c) =>
        (filtros.status === "todas" || c.ativa === (filtros.status === "ativas")) &&
        (!termo || chaveDescricao(c.descricao).includes(termo)),
    );
  }, [cargas, filtros]);

  useEffect(() => {
    setTotal(filtered.length);
  }, [filtered, setTotal]);

  const paged = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const applyFilters = () => {
    setFiltros(inputValues);
    setPage(0);
  };

  const handleClearFilters = () => {
    setInputValues(filtrosIniciais);
    setFiltros(filtrosIniciais);
    setPage(0);
  };

  const activeFilterCount = (filtros.descricao ? 1 : 0) + (filtros.status !== "todas" ? 1 : 0);

  const abrirCriacao = () => {
    setTentouSalvar(false);
    handleCreate();
  };

  const abrirEdicao = (carga: CargaHoraria) => {
    setTentouSalvar(false);
    handleEdit(carga);
  };

  const onSave = async (values: Partial<CargaHoraria>) => {
    setTentouSalvar(true);
    const [primeiroErro] = validarCargaHoraria({ ...values, id: editing?.id }, cargas);
    if (primeiroErro) {
      notify(capitalizar(primeiroErro.completa), "error");
      return;
    }
    try {
      const result = await saveCargaHoraria(
        {
          id: editing?.id,
          descricao: values.descricao ?? "",
          cor: corParaEnvio(values.cor),
          util: blocoParaEnvio(values.util)!,
          sabado: blocoParaEnvio(values.sabado),
          domingo: blocoParaEnvio(values.domingo),
          feriado: blocoParaEnvio(values.feriado),
        },
        clientId,
      );
      log(editing ? LogEventType.Updated : LogEventType.Created, result, "escala_carga_horaria", result.id, "info");
      const aviso = mensagemDoReflexoDeCarga(`Carga horária ${editing ? "atualizada" : "criada"}!`, result);
      notify(aviso.texto, aviso.tipo);
      recarregar();
      handleClose();
    } catch (e: any) {
      notify(capitalizar(e?.message || "Erro ao salvar carga horária"), "error");
    }
  };

  const onAlternarConfirm = async () => {
    if (!alternando?.id) return;
    try {
      const ativa = !alternando.ativa;
      const result = await ativarCargaHoraria(alternando.id, ativa, clientId);
      log(ativa ? LogEventType.Activated : LogEventType.Deactivated, result, "escala_carga_horaria", alternando.id, "info");
      notify(ativa ? "Carga horária ativada!" : "Carga horária inativada!", "success");
      recarregar();
    } catch (e: any) {
      notify(capitalizar(e?.message || "Erro ao atualizar status"), "error");
    } finally {
      setAlternando(null);
    }
  };

  const onExcluirConfirm = async () => {
    if (!excluindo?.id) return;
    try {
      await excluirCargaHoraria(excluindo.id, clientId);
      log(LogEventType.Deleted, excluindo, "escala_carga_horaria", excluindo.id, "info");
      notify("Carga horária excluída!", "success");
      recarregar();
    } catch (e: any) {
      notify(capitalizar(e?.message || "Erro ao excluir carga horária"), "error");
    } finally {
      setExcluindo(null);
    }
  };

  // Confere na tela contra a lista inteira, inativas inclusive, antes de ir ao servidor.
  const handleImportar = async (linhas: LinhaPlanilha[]): Promise<ResultadoImportacao> => {
    const { cargas: novas, erros } = resolverCargasParaImportar(linhas, cargas);
    if (erros.length > 0) return { importados: 0, erros };
    return importarCargasHorarias(novas, clientId);
  };

  if (!carregouAlgumaVez) return <Sync />;

  return (
    <>
      <CrudToolbar>
        <FilterModal activeCount={activeFilterCount} onApply={applyFilters} onClear={handleClearFilters}>
          <TextField
            label="Descrição"
            size="small"
            fullWidth
            value={inputValues.descricao}
            onChange={(e) => setInputValues((v) => ({ ...v, descricao: e.target.value }))}
          />
          <TextField
            label="Status"
            size="small"
            fullWidth
            select
            value={inputValues.status}
            onChange={(e) => setInputValues((v) => ({ ...v, status: e.target.value as FiltroStatus }))}
          >
            <MenuItem value="todas">Todas</MenuItem>
            <MenuItem value="ativas">Ativas</MenuItem>
            <MenuItem value="inativas">Inativas</MenuItem>
          </TextField>
        </FilterModal>
        <Stack direction="row" spacing={2}>
          <ImportadorPlanilha
            titulo="Importar cargas horárias"
            rotuloBotao="Importar cargas horárias"
            nomeArquivoModelo="modelo_cargas_horarias.xlsx"
            colunas={colunasImportacaoCargas}
            onImportar={handleImportar}
            onConcluido={recarregar}
          />
          <Button variant="contained" startIcon={<Add />} onClick={abrirCriacao}>
            Adicionar carga horária
          </Button>
        </Stack>
      </CrudToolbar>

      <DataTable
        data={paged}
        columns={[
          {
            key: "ativa",
            label: "Ativa",
            render: (c) => <Checkbox checked={c.ativa} disabled />,
            exportValue: (c) => (c.ativa ? "Sim" : "Não"),
          },
          {
            key: "descricao",
            label: "Descrição",
            render: (c) => (
              <Stack direction="row" spacing={1} alignItems="center">
                <span>{c.descricao}</span>
                <SeloIntegracao sistema={c.travado_por} />
              </Stack>
            ),
            exportValue: (c) => c.descricao,
          },
          {
            key: "cor",
            label: "Cor da jornada",
            render: (c) =>
              c.cor ? (
                <Box
                  aria-label={`Cor ${c.cor}`}
                  sx={{ width: 24, height: 24, borderRadius: 0.5, bgcolor: c.cor, border: 1, borderColor: "divider" }}
                />
              ) : (
                "Padrão"
              ),
            exportValue: (c) => c.cor || "Padrão",
          },
          ...BLOCOS.map((bloco) => ({
            key: bloco,
            label: ROTULO_BLOCO[bloco],
            render: (c: CargaHoraria) => formatarBloco(c[bloco]),
            exportValue: (c: CargaHoraria) => formatarBloco(c[bloco]),
          })),
          { key: "vinculados", label: "Colaboradores" },
        ]}
        total={total}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
        actions={(carga) => (
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <IconButton aria-label={`Editar ${carga.descricao}`} onClick={() => abrirEdicao(carga)}>
              <Edit />
            </IconButton>
            <IconButton
              aria-label={`${carga.ativa ? "Inativar" : "Ativar"} ${carga.descricao}`}
              onClick={() => setAlternando(carga)}
              color={carga.ativa ? "warning" : "success"}
            >
              {carga.ativa ? <Block /> : <CheckCircle />}
            </IconButton>
            {podeExcluirCarga(carga) && (
              <IconButton aria-label={`Excluir ${carga.descricao}`} onClick={() => setExcluindo(carga)} color="error">
                <Delete />
              </IconButton>
            )}
          </Stack>
        )}
      />

      <CrudModal<CargaHoraria, { existentes: CargaHoraria[]; mostrarErros: boolean }>
        open={open}
        onClose={handleClose}
        title={editing ? "Editar carga horária" : "Adicionar carga horária"}
        initialData={editing}
        onSubmit={onSave}
        Form={CargaHorariaForm}
        extraFormProps={{ existentes: cargas, mostrarErros: tentouSalvar }}
      />

      <ConfirmModal
        open={!!alternando}
        onClose={() => setAlternando(null)}
        onConfirm={onAlternarConfirm}
        title={alternando?.ativa ? "Inativação" : "Ativação"}
        btnText="Confirmar"
        text={
          alternando?.ativa
            ? `Deseja inativar a carga horária ${alternando?.descricao}? Quem já tem a carga continua com ela; ela só deixa de ser oferecida.`
            : `Deseja ativar a carga horária ${alternando?.descricao}?`
        }
      />

      <ConfirmModal
        open={!!excluindo}
        onClose={() => setExcluindo(null)}
        onConfirm={onExcluirConfirm}
        title="Exclusão"
        btnText="Confirmar"
        text={`Deseja excluir a carga horária ${excluindo?.descricao}?`}
      />
    </>
  );
}
